import { t } from "i18next";
import StatdataItem from "../../../../components/items/StatCardItem";
import { useQuery } from "@tanstack/react-query";
import { adminDashboard } from "../../../../services/adminDashboardService";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

const Dashboard = () => {
  const { lang } = useSelector((state) => state?.langProvider);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const res = await adminDashboard();
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.data;
    },
  });

  const statsCards = [
    {
      id: "organization",
      icon: "fa-solid fa-building",
      title: t("Organizations"),
      value: data?.totalOrganization,
      description: `Total organizations registered →`,
      link: `/${lang}/admin/organization`,
      showButton: true,
    },
    {
      id: "total-users",
      icon: "fa-regular fa-users",
      title: t("Total Users"),
      value: data?.totalUsers,
      description: `${t("Total registered users on the platform →")}`,
      link: `/${lang}/admin/users`,
      showButton: true,
      progress: "77%",
    },
    {
      id: "total-artists",
      icon: "fa-solid fa-user-music",
      title: t("Total Artists"),
      value: data?.totalArtist,
      description: `${t("Total artists associated with events →")}`,
      link: `/${lang}/admin/artist`,
      showButton: true,
    },
    {
      id: "total-events",
      icon: "fa-solid fa-calendar-days",
      title: t("Total Events"),
      value: data?.totalEvents,
      description: `${t("Total number of events created →")}`,
      link: `/${lang}/admin/events`,
      showButton: true,
    },
    {
      id: "total-tickets",
      icon: "fa-regular fa-ticket-alt",
      title: t("Total Tickets"),
      value: data?.totalTickets,
      description: `${t("Number of different ticket available →")}`,
      link: `/${lang}/admin/tickets`,
      showButton: true,
      progress: "77%",
    },
    {
      id: "page-views",
      icon: "fa-solid fa-puzzle-piece",
      title: t("Total Addons"),
      value: data?.totalAddOns,
      description: "Total add-ons available for events",
      // link:`/en/admin/tickets`,
      showButton: true,
    },
    {
      id: "ticket-sales",
      icon: "fa-regular fa-chart-line",
      title: t("Total Ticket Sales"),
      value: data?.totalTicketSales,
      description: `Total tickets sold across all events →`,
      link: `/${lang}/admin/tickets`,
      showButton: true,
    },
    {
      id: "total-orders",
      icon: "fa-regular fa-box",
      title: t("Total Orders"),
      value: data?.totalOrders,
      description: "Number of customer orders processed →",
      link: `/${lang}/admin/order`,
      showButton: true,
    },
  ];

  return (
    <div className="bg-white py-4 px-4 rounded-md">
      <div className="relative z-10">
        <div className="w-full mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="space-y-4 w-full py-2">
              <div className="flex items-center justify-between border-b border-gray-300 pb-4">
                <div className="flex justify-between">
                  <div className="flex flex-wrap items-center gap-4 flex-shrink">
                    {/* <div className="w-12 md:w-16 aspect-square bg-primary/10 rounded-xl flex items-center justify-center text-primary text-3xl">
                      <i className="fa-light fa-calendar-star"></i>
                    </div> */}
                    <div>
                      <h1 className="text-xl lg:text-2xl font-bold text-deep-purple font-Montserrat">{t("Dashboard")}</h1>
                      <p className="text-sm text-gray-500">Overview of your event performance</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 mb-8">
            {statsCards.map((card) => (
              <StatdataItem key={card.id} data={card} loading={false} />
            ))}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-8">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-xl font-semibold text-deep-purple mb-5">Upcoming Events</h2>

              <div className="space-y-4">
                {data?.upcomingEvents?.map((ev) => {
                  const start = new Date(ev.startDate);
                  const end = new Date(ev.endDate);

                  const day = start.toLocaleDateString("en-US", { weekday: "short" });
                  const month = start.toLocaleDateString("en-US", { month: "short" });
                  const date = start.getDate();
                  const year = start.getFullYear();

                  const startsIn = Math.ceil((start - new Date()) / (1000 * 60 * 60 * 24));
                  const statusColors = {
                    online: "bg-green-100 text-green-600",
                    offline: "bg-blue-100 text-blue-600",
                    notspecified: "bg-gray-100 text-gray-500",
                  };

                  return (
                    <div
                      key={ev._id}
                      className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:shadow-[0_4px_12px_rgba(255,165,0,0.35)] transition-all"
                    >
                      <div className="w-16 h-16 rounded-xl bg-primary/10 text-primary flex flex-col items-center justify-center leading-tight">
                        <span className="text-xs font-medium">{day}</span>
                        <span className="text-xl font-bold">{date}</span>
                        <span className="text-[10px] uppercase -mt-1">{month}</span>
                        <span className="text-[10px] text-primary font-semibold">{year}</span>
                      </div>
                      {!ev.image?.url ? (
                        <div className="w-20 h-20 rounded-xl border bg-primary flex items-center justify-center">
                          <i className="fa-regular fa-image text-secondary text-xl"></i>
                        </div>
                      ) : (
                        <img src={ev.image?.url} className="w-20 h-20 rounded-xl object-cover border" />
                      )}

                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-deep-purple hover:underline">{ev.title?.en || ev.title}</h3>

                        <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                          <img src={ev.organization?.logo?.url} className="w-6 h-6 rounded-full border object-cover" />
                          <span>{ev.organization?.name?.en}</span>
                        </div>

                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <i className="fa-regular fa-calendar"></i>
                            {start.toLocaleDateString("en-US", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </div>
                          <span
                            className={`px-2 py-1 rounded-full text-[11px] font-semibold capitalize ${
                              statusColors[ev.locationStatus] || statusColors.notspecified
                            }`}
                          >
                            {ev.locationStatus}
                          </span>
                        </div>
                        <p className="text-xs mt-2 text-primary font-medium">Starts in {startsIn} days</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-xl font-semibold text-deep-purple mb-5">Recent Orders</h2>

              <div className="space-y-4">
                {data?.recentOrders?.map((o) => {
                  const created = new Date(o.createdAt);

                  const now = new Date();
                  const diffSeconds = Math.floor((now - created) / 1000);
                  let timeAgo = "";
                  if (diffSeconds < 60) timeAgo = `${diffSeconds}s ago`;
                  else if (diffSeconds < 3600) timeAgo = `${Math.floor(diffSeconds / 60)}m ago`;
                  else if (diffSeconds < 86400) timeAgo = `${Math.floor(diffSeconds / 3600)}h ago`;
                  else if (diffSeconds < 2592000) timeAgo = `${Math.floor(diffSeconds / 86400)}d ago`;
                  else if (diffSeconds < 31104000) timeAgo = `${Math.floor(diffSeconds / 2592000)}mo ago`;
                  else timeAgo = `${Math.floor(diffSeconds / 31104000)}y ago`;

                  const eventStart = new Date(o.event?.startDate);

                  return (
                    <div
                      key={o._id}
                      className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:shadow-[0_4px_12px_rgba(255,165,0,0.35)] transition-all"
                    >
                      <img
                        src={o.user?.image?.url}
                        alt={`${o.user.firstName} ${o.user.lastName}`}
                        className="w-12 h-12 rounded-full object-cover border"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-deep-purple text-sm">{o.orderId}</h3>
                          <span className="text-sm font-semibold text-deep-purple">
                            {o.event?.currency?.symbol} {o.total}
                          </span>
                        </div>

                        <p className="text-xs text-gray-500 mt-1">
                          {o.user.firstName} {o.user.lastName}
                        </p>
                        <div className="mt-2 flex items-center gap-3">
                          {o.event?.firstImage?.url ? (
                            <img
                              src={o.event.firstImage.url}
                              className="w-14 h-14 rounded-lg border object-cover"
                              alt={o.event?.title?.en || o.event?.title}
                            />
                          ) : (
                            <div className="w-14 h-14 rounded-lg border bg-primary flex items-center justify-center">
                              <i className="fa-light fa-image text-[#000] text-xl"></i>
                            </div>
                          )}

                          <div className="text-xs text-gray-600">
                            <p className="font-medium text-sm text-deep-purple">{o.event?.title?.en || o.event?.title}</p>

                            <div className="flex items-center gap-1 mt-1">
                              <i className="fa-regular fa-calendar"></i>
                              {eventStart.toLocaleDateString("en-US", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 mt-3">
                          <span
                            className={`px-3 py-1 text-xs rounded-full ${
                              o.orderStatus === "confirmed"
                                ? "bg-blue-100 text-blue-700"
                                : o.orderStatus === "processing"
                                ? "bg-purple-100 text-purple-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {o.orderStatus}
                          </span>

                          <span className="text-xs text-gray-500 ml-auto">{timeAgo}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
